import React, { useRef } from 'react';
import './cro-sprint.css';

import useScrollReveal from '../hooks/useScrollReveal';
import usePageMeta from '../hooks/usePageMeta';

import SprintHero from '../components/cro/sprint-hero';
import SprintFeatureGrid from '../components/cro/sprint-feature-grid';
import SprintProcessGrid from '../components/cro/sprint-process-grid';
import SprintTags from '../components/cro/sprint-tags';
import SprintFlexCards from '../components/cro/sprint-flex-cards';
import SprintPillCards from '../components/cro/sprint-pill-cards';
import SprintCta from '../components/cro/sprint-cta';

import {
    IconSearch,
    IconClipboard,
    IconTool,
    IconBarChart,
    IconTarget,
    IconSmartphone,
    IconFileText,
    IconDatabase,
    IconZap,
    IconHeart,
    IconTrendingUp,
    IconShoppingBag,
    IconBox,
    IconGlobe,
    IconMonitor
} from '../components/sprint-icons';

export default function ConversionLandingPages() {
    const pageRef = useRef(null);

    useScrollReveal(pageRef);
    usePageMeta(
        'Conversion Landing Page Design for eCommerce | Spinnennetz',
        'Maximize your ad spend with custom conversion landing pages. Spinnennetz designs pages built for one goal — turning your traffic into paying customers.',
        '/conversion-landing-pages',
        { "@context": "https://schema.org", "@type": "Service", "name": "Conversion Landing Page Design", "provider": { "@type": "Organization", "name": "Spinnennetz" } }
    );

    const whoWeHelpItems = [
        { icon: <IconShoppingBag />, title: 'Shopify Brands', desc: 'Increase revenue with landing pages tailored to your products, promotions, and customer journey.' },
        { icon: <IconBox />, title: 'D2C Brands', desc: 'Convert more paid traffic into customers with persuasive messaging, strong visual hierarchy, and optimized user experiences.' },
        { icon: <IconGlobe />, title: 'Lead Generation Businesses', desc: 'Generate more qualified leads through focused landing pages designed to maximize form submissions and inquiries.' },
        { icon: <IconTarget />, title: 'Product Launches', desc: 'Launch new products with dedicated pages that educate, build trust, and drive immediate action.' },
    ];

    const processSteps = [
        { num: 1, icon: <IconSearch />, title: 'Research & Strategy', desc: 'We analyze your audience, offer, competitors, and traffic sources.' },
        { num: 2, icon: <IconClipboard />, title: 'Wireframing', desc: 'We structure the page around user intent and conversion goals.' },
        { num: 3, icon: <IconTool />, title: 'Design & Development', desc: 'Pixel-perfect landing pages built for speed, usability, and performance.' },
        { num: 4, icon: <IconBarChart />, title: 'Testing & Optimization', desc: 'We continuously improve conversion rates through experimentation and data-driven insights.' },
    ];

    const whatsIncludedList = [
        'Conversion-focused landing page design',
        'Mobile-first responsive development',
        'Shopify integration',
        'Lead capture forms',
        'Product storytelling sections',
        'Trust signals and social proof',
        'A/B testing ready structure',
        'Fast-loading performance optimization',
        'Analytics and tracking setup',
        'CTA optimization',
    ];

    const whyChooseItems = [
        { icon: <IconTarget />, title: 'Built Around Conversions', desc: 'Every section, headline, and CTA is designed to move visitors toward action.' },
        { icon: <IconZap />, title: 'Fast Turnaround', desc: 'Launch campaigns quickly without sacrificing quality.' },
        { icon: <IconDatabase />, title: 'Data-Driven Decisions', desc: 'We combine UX best practices with CRO principles to maximize results.' },
        { icon: <IconTrendingUp />, title: 'Shopify & D2C Expertise', desc: 'Deep experience working with eCommerce brands and performance marketing teams.' },
    ];

    const idealFor = [
        { icon: <IconMonitor />, label: 'Facebook & Instagram Ads' },
        { icon: <IconTarget />, label: 'Google Ads Campaigns' },
        { icon: <IconBox />, label: 'Product Launches' },
        { icon: <IconHeart />, label: 'Seasonal Promotions' },
        { icon: <IconGlobe />, label: 'Lead Generation Funnels' },
        { icon: <IconFileText />, label: 'Email Marketing Campaigns' },
        { icon: <IconSmartphone />, label: 'Influencer Traffic Campaigns' },
    ];

    const heroMetrics = [
        { icon: <IconTrendingUp />, value: 'Sales', label: 'Increase Revenue' },
        { icon: <IconGlobe />, value: 'Leads', label: 'Generate Inquiries' },
        { icon: <IconSmartphone />, value: 'Mobile', label: 'Optimized Flow' },
        { icon: <IconTarget />, value: 'Goal', label: 'Conversion Focus' },
    ];

    return (
        <div className="cro-sprint-page" ref={pageRef}>
            <SprintHero
                badgeText="Conversion Landing Pages"
                titlePrefix="Turn More Visitors"
                highlightText="Into Customers"
                titleSuffix=""
                subtitle="Your ads are driving traffic. Your products are ready. But are your landing pages converting?"
                subtitle2="We design and build high-converting landing pages that help Shopify and D2C brands increase sales, generate qualified leads, and launch products with confidence. Whether you're running paid campaigns, launching a new product, or optimizing an existing funnel, we create landing pages built for one purpose: conversion."
                primaryButtonText="Book a Free Strategy Call"
                secondaryButtonText="Who We Help"
                secondaryHref="#who-we-help"
                metrics={heroMetrics}
            />

            <SprintFeatureGrid
                sectionId="who-we-help"
                label="Who We Help"
                titlePrefix="Tailored for your"
                highlightText="business goals"
                titleSuffix=""
                items={whoWeHelpItems}
                gridClass="grid-4"
            />

            <SprintTags
                label="What's Included"
                titlePrefix="Everything needed for"
                highlightText="high conversion"
                titleSuffix=""
                items={whatsIncludedList}
                variant="check"
            />

            <SprintProcessGrid
                label="Our Process"
                titlePrefix="Four steps to"
                highlightText="success"
                titleSuffix=""
                steps={processSteps}
            />

            <SprintFlexCards
                label="Why Choose Us"
                titlePrefix="What makes us"
                highlightText="different"
                titleSuffix=""
                items={whyChooseItems}
                accentVariant="bottom"
            />

            <SprintPillCards
                label="Perfect For"
                titlePrefix="Built for campaigns"
                highlightText="ready to scale"
                titleSuffix=""
                items={idealFor}
                hoverDirection="right"
            />

            <SprintCta
                title="Ready to Increase Conversions?"
                subtitle="Let's build landing pages that turn traffic into customers and clicks into revenue."
                buttonText="Book a Free Strategy Call"
            />
        </div>
    );
}
